import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from "@angular/core";
import { UnitConversionService, UnitConversionType } from "@app/services/unitConversion.service";
import { DynamicReportService } from "@reports/_services/dynamicReports.service";
import { IReportDefinition } from "@reports/models/IReportDefinition";
import { IReportParameter, IReportParameterDefinition, ReportParameterType, ReportParameterValueType } from "@reports/models/IReportParameters";
import { ITemperatureProbe } from "@temperature/_models/temperature/ITemperatureProbe";
import { TemperatureProbeService } from "@temperature/_services/temperature-probe.service";
import "./report-parameters.component.scss";
import { lastValueFrom } from "rxjs";

interface IDisplayTemperatureProbeParameter {
    displayName: string;
    source: number;
    position: number;
    enabled?: boolean;
}

interface ITemperatureProbeParamaterValue {
    Source: number;
    Position: number;
}

@Component({
    selector: "report-parameters",
    template: require("./report-parameters.component.html")
})
export class ReportParametersComponent implements OnChanges {

    @Input() report: IReportDefinition;
    @Output() reportChange = new EventEmitter<IReportDefinition>();

    // ---

    customParameterDefinitions: IReportParameterDefinition[];
    ReportParameterValueType = ReportParameterValueType;
    ReportParameterType = ReportParameterType;
    UnitConversionType = UnitConversionType;

    probes: ITemperatureProbe[] = [];
    probesForVehicle: ITemperatureProbe[] = [];
    displayProbes: IDisplayTemperatureProbeParameter[] = [];

    cachedVehicleId: number;
    completedFirstChange: boolean;

    constructor(
        private readonly dynamicReportService: DynamicReportService,
        private readonly temperatureProbeService: TemperatureProbeService,
        private readonly unitConversionService: UnitConversionService
    ) {
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes["report"]) {
            this.setupParametersFromReport();
        }
    }

    processI18nOptionsChange(): void {

        if (!this.customParameterDefinitions?.length) {
            return;
        }

        this.customParameterDefinitions.forEach(paramDefinition => {

            if (paramDefinition.valueType !== ReportParameterValueType.Number) {
                return;
            }

            if (!paramDefinition.conversionType && paramDefinition.conversionType !== 0) {
                paramDefinition.conversionType = UnitConversionType.Unknown;
            }

            const newConversionLabel = this.unitConversionService.getConversionLabel(paramDefinition.conversionType, this.report.cachedCustomPreferences);
            const labelIsMetric = this.unitConversionService.isLabelMetric(paramDefinition.conversionLabel);

            paramDefinition.conversionLabel = newConversionLabel;
            paramDefinition.Value = (labelIsMetric) ? paramDefinition.displayValue : this.unitConversionService.convert(paramDefinition.displayValue, paramDefinition.conversionType, 0, true, -1, this.report.cachedCustomPreferences);

            this.onCustomParameterChanged(paramDefinition);
        })
    }

    processVehicleChange(): void {
        this.clearCachedProbes();
        this.clearProbeParameters();

        this.processVehicle();
    }

    onNumberParameterChanged(newValue: number | string, paramDefinition: IReportParameterDefinition): void {

        if (typeof newValue !== "undefined" && newValue !== "") {
            paramDefinition.displayValue = +newValue;
        }

        const labelIsMetric = this.unitConversionService.isLabelMetric(paramDefinition.conversionLabel);
        paramDefinition.Value = (labelIsMetric) ? paramDefinition.displayValue : this.unitConversionService.convert(paramDefinition.displayValue, paramDefinition.conversionType, 0, true, -1, this.report.cachedCustomPreferences);

        this.onCustomParameterChanged(paramDefinition);
    }

    onSelectParameterChanged(newValue: any, definition: IReportParameterDefinition): void {
        definition.Value = newValue;

        this.onCustomParameterChanged(definition);
    }

    onTemperatureProbeChanged(definition: IReportParameterDefinition): void {

        definition.Value = [];

        if (!this.displayProbes?.length) {
            return;
        }

        this.displayProbes.forEach(displayProbe => {
            if (displayProbe.enabled) {
                return;
            }

            const newValue: ITemperatureProbeParamaterValue = {
                Position: displayProbe.position,
                Source: displayProbe.source
            }

            definition.Value.push(newValue);
        });

        this.onCustomParameterChanged(definition);
    }

    onCustomParameterChanged(changedParameter: IReportParameterDefinition): void {

        if (changedParameter.valueType === ReportParameterValueType.Boolean) {
            changedParameter.Value = changedParameter.Active;
        }

        this.updateCustomParameters();
        this.emitChange();
    }

    // ---

    private setupParametersFromReport(): void {
        this.customParameterDefinitions = this.dynamicReportService.getCustomParameterDefinitions(this.report);
        this.prepare();
    }

    private updateCustomParameters(): void {
        if (!this.customParameterDefinitions?.length) {
            return;
        }
        
        this.customParameterDefinitions.forEach(def => {
            const existingIndex = this.report.Parameters.CustomParameters.findIndex(p => p.ParameterType === def.ParameterType);

            if (def.Active) {
                const newParam: IReportParameter = {
                    ParameterType: def.ParameterType,
                    Value: def.Value,
                    Active: def.Active
                };

                existingIndex > -1 ?
                    this.report.Parameters.CustomParameters[existingIndex] = newParam :
                    this.report.Parameters.CustomParameters.push(newParam);

            } else if (existingIndex > -1) {
                this.report.Parameters.CustomParameters.splice(existingIndex, 1);
            }            
        });
    }

    private fixParameterDefinitions(customParameterDefinitions: IReportParameterDefinition[]): void {
        if (!customParameterDefinitions?.length) {
            return;
        }

        // set missing value based on default value
        customParameterDefinitions.forEach(customParameterDefinition => {
            if (!customParameterDefinition.Active && typeof customParameterDefinition.Value === "undefined") {
                customParameterDefinition.Value = customParameterDefinition.Value || customParameterDefinition.defaultValue;
            }

            if (customParameterDefinition.Value) {
                customParameterDefinition.displayValue = this.unitConversionService.convert(customParameterDefinition.Value, customParameterDefinition.conversionType);
            }
        });
    }

    private emitChange(): void {
        this.reportChange.emit(this.report);
    }

    private clearCachedProbes(): void {
        this.probesForVehicle = [];
        this.displayProbes = [];
    }

    private clearProbeParameters(): void {
        const temperatureProbeParamDefinition = this.customParameterDefinitions.find(paramDefinition => paramDefinition.valueType === ReportParameterValueType.TemperatureProbes);

        if (!temperatureProbeParamDefinition) {
            return;
        }

        temperatureProbeParamDefinition.Value = [];

        this.onCustomParameterChanged(temperatureProbeParamDefinition);
    }

    private prepare(): void {
        this.fixParameterDefinitions(this.customParameterDefinitions);
        this.processVehicle();
    }

    private async processVehicle(): Promise<void> {
        this.clearCachedProbes();

        const vehicleId: number = this.report?.Parameters?.VehicleId;

        if (!vehicleId && vehicleId !== 0 && vehicleId !== this.cachedVehicleId) {
            console.warn("No vehicle id, bailing");
            return;
        }

        this.cachedVehicleId = vehicleId;

        await this.processTemperatureProbes();
    }

    private async processTemperatureProbes(): Promise<void> {
        const temperatureProbeParamDefinition = this.customParameterDefinitions.find(paramDefinition => paramDefinition.valueType === ReportParameterValueType.TemperatureProbes);

        if (!temperatureProbeParamDefinition) {
            return;
        }

        if (!this.probes?.length) {
            this.probes = await lastValueFrom(this.temperatureProbeService.getTemperatureProbes());
        }

        this.probesForVehicle = this.probes?.filter(probe => probe.VehicleId === this.report.Parameters.VehicleId) ?? [];

        this.setDisplayProbes(temperatureProbeParamDefinition);
    }

    private setDisplayProbes(temperatureProbeParamDefinition: IReportParameterDefinition): void {

        this.displayProbes = [];

        if (!this.probesForVehicle?.length) {
            return;
        }

        this.probesForVehicle.forEach(probe => {
            const matchingProbe: ITemperatureProbeParamaterValue = this.findProbeInDefinition(temperatureProbeParamDefinition, probe);

            const newMapping: IDisplayTemperatureProbeParameter = {
                displayName: probe.Name || probe.Code,
                source: probe.Source,
                position: probe.Position,
                enabled: !matchingProbe
            }

            this.displayProbes.push(newMapping);
        });
    }

    private findProbeInDefinition(temperatureProbeParamDefinition: IReportParameterDefinition, probe: ITemperatureProbe): ITemperatureProbeParamaterValue {

        if (!temperatureProbeParamDefinition?.Value?.length) {
            return;
        }

        const values: ITemperatureProbeParamaterValue[] = temperatureProbeParamDefinition.Value;

        return values.find(value => value.Position === probe.Position && value.Source === probe.Source);
    }
}
